import { InfoTooltip } from '@/components/InfoTooltip'
import { NumericField } from '@/components/NumericField'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import { rangeError } from '@/lib/settingRanges'
import type { GlobalChunking } from '@/types/bundle'

export interface ChunkingTabProps {
  chunking: GlobalChunking
  onChange: (chunking: GlobalChunking) => void
}

// Shown when a setting is left blank: the engine then uses its own built-in
// value (backend PRD 40) - not repeated here, so it can never drift.
const BUILT_IN = 'Built-in default'

// Platform-wide chunking (global_defaults.chunking, backend PRD 40): how a
// document's pages are split into model calls and how many of those calls
// run at once. A document type can override the target on its General tab.
export function ChunkingTab({ chunking, onChange }: ChunkingTabProps) {
  function update(patch: Partial<GlobalChunking>) {
    onChange({ ...chunking, ...patch })
  }

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Chunking
        <InfoTooltip text="A document's pages are sent to the model in chunks of roughly equal size. The chunks of one document are sent at the same time, so a long document takes about as long as its slowest chunk." />
      </h2>

      <div className="grid grid-cols-2 gap-4">
        <NumericField
          id="chunking-target-pages"
          label="Target pages per chunk"
          tooltip="The most pages in one model call. Blank uses the extractor's max pages (normally 10); never more than that. Smaller chunks run faster in parallel but repeat the prompt, so they cost more input tokens."
          min={1}
          step="1"
          placeholder={BUILT_IN}
          value={numberField(chunking.target_pages_per_chunk_default)}
          error={rangeError('target_pages_per_chunk_default', chunking.target_pages_per_chunk_default)}
          onChange={(raw) => update({ target_pages_per_chunk_default: parseOptionalNumber(raw) })}
        />
        <NumericField
          id="chunking-max-concurrent"
          label="Max concurrent chunks"
          tooltip="How many of one document's chunks are sent to the model at the same time (1 to 10). 1 sends them one after another. Not overridable per document type."
          min={1}
          max={10}
          step="1"
          placeholder={BUILT_IN}
          value={numberField(chunking.max_concurrent_chunks)}
          error={rangeError('max_concurrent_chunks', chunking.max_concurrent_chunks)}
          onChange={(raw) => update({ max_concurrent_chunks: parseOptionalNumber(raw) })}
        />
      </div>
    </section>
  )
}
